(ns practitest.frontend.components2.dashboard.charts.label-layout
  "Pie label layout: each side of the pie holds a staircase of labels that
   follows the label ring, top to bottom in slice order.

             ╭─── Label A
         ╭───╯ ╭── Label B
        ╱ pie ─╯
        ╲     ───── Label C

   Each label ideally sits on its slice's mid-angle ray, at the first
   point where its text clears the ring, so its connector is one straight
   radial line. Labels moved to make room keep that ray while it reaches
   their height, then turn horizontal to the label.

   Guarantees:
   - labels on one side are at least `spacing` apart vertically, so they
     never overlap each other;
   - every label's text box lies fully outside the label ring;
   - a connector either runs inside the ring, or along its own slice's
     ray just past it, and ends horizontally at the label's own height,
     so it doesn't cross another label's text.")

(defn- distribute
  "Heights for labels in top-to-bottom order, as close as possible to
   their ideal heights while keeping them at least `spacing` apart and
   inside [y-min, y-max].

   Labels that are too close form a group, spaced `spacing` apart and
   centered on its members' ideal heights. When a group then runs into
   the one above it, the two merge and re-center. If a side holds more
   labels than fit, the top edge wins and the lowest labels overflow."
  [ideals spacing y-min y-max]
  (let [clamp  (fn [start n] (max y-min (min start (- y-max (* (dec n) spacing)))))
        groups (reduce
                (fn [groups ideal]
                  (loop [groups (conj groups {:sum ideal :n 1 :start (clamp ideal 1)})]
                    (let [c (count groups)]
                      (if (< c 2)
                        groups
                        (let [above (nth groups (- c 2))
                              below (peek groups)]
                          (if (> (+ (:start above) (* (:n above) spacing)) (:start below))
                            (let [n   (+ (:n above) (:n below))
                                  sum (+ (:sum above) (:sum below))
                                  ;; Start that minimizes the members' squared
                                  ;; distance from their ideal heights
                                  start (- (/ sum n) (/ (* (dec n) spacing) 2))]
                              (recur (conj (pop (pop groups))
                                           {:sum sum :n n :start (clamp start n)})))
                            groups))))))
                []
                ideals)]
    (mapcat (fn [{:keys [start n]}]
              (map (fn [i] (+ start (* i spacing))) (range n)))
            groups)))

(defn- ring-x
  "Distance from the vertical axis to the ring of radius r at height y
   (0 above/below the ring)."
  [r y]
  (js/Math.sqrt (max 0 (- (* r r) (* y y)))))

(defn- anchor-x
  "Distance from the vertical axis at which a label at height y starts so
   that its whole text box clears the ring. The ring is widest at the
   box's edge nearest the pie's horizontal midline, so that edge decides."
  [ring-radius label-height gap y]
  (let [half (/ label-height 2)
        edge (cond
               (< y (- half)) (+ y half)
               (> y half)     (- y half)
               :else          0)]
    (+ gap (ring-x ring-radius edge))))

(defn- stagger
  "Where labels are crowded, the ring alone can leave their anchors almost
   directly above each other (the ring is nearly vertical beside the pie).
   Steps each label in a run of adjacent labels at least `step` further
   out than its neighbor nearer the pie's top or bottom, continuing the
   ring's staircase: outward going down in the upper half, outward going
   up in the lower half. Connectors just get a longer horizontal end.

   ds — anchor distances from the vertical axis, ys — heights, both in
   top-to-bottom order."
  [ds ys spacing step]
  (let [n         (count ds)
        adjacent? (fn [i j] (< (abs (- (nth ys i) (nth ys j))) (* 1.5 spacing)))
        push-out  (fn [ds i from]
                    (assoc ds i (max (nth ds i) (+ (nth ds from) step))))
        downward  (reduce (fn [ds i]
                            (if (and (neg? (nth ys i)) (adjacent? (dec i) i))
                              (push-out ds i (dec i))
                              ds))
                          (vec ds)
                          (range 1 n))]
    (reduce (fn [ds i]
              (if (and (pos? (nth ys i)) (adjacent? i (inc i)))
                (push-out ds i (inc i))
                ds))
            downward
            (range (- n 2) -1 -1))))

(def ^:private tau (* 2 js/Math.PI))

(defn- ray-anchor-t
  "Distance along a slice's mid-angle ray to the first point where a
   label anchored there has its whole text box outside the ring — the
   same rule anchor-x follows. Straight above/below the pie the ray never
   gets there (text always starts `gap` off the axis), so it's capped
   once the text is clear of the ring vertically."
  [{:keys [ring-radius label-height gap]} angle]
  (let [sin    (abs (js/Math.sin angle))
        cos    (abs (js/Math.cos angle))
        half   (/ label-height 2)
        clear? (fn [t]
                 (let [x (- (* t sin) gap)
                       e (max 0 (- (* t cos) half))]
                   (and (>= x 0) (>= (js/Math.hypot x e) ring-radius))))
        t-max  (+ ring-radius label-height gap)]
    (if (clear? t-max)
      (loop [lo ring-radius, hi t-max, i 0]
        (if (= i 30)
          hi
          (let [mid (/ (+ lo hi) 2)]
            (if (clear? mid) (recur lo mid (inc i)) (recur mid hi (inc i))))))
      t-max)))

(defn- ideal-y [ctx {:keys [angle]}]
  (- (* (ray-anchor-t ctx angle) (js/Math.cos angle))))

(defn- bend-x
  "Where a label's connector turns horizontal at the label's height y:
   on the slice's own ray if that ray reaches height y before the label's
   anchor (for a label at its ideal spot, that's the anchor itself — one
   straight radial line), otherwise on the ring, like a label pushed away
   from its slice's height or moved to the other side."
  [ring-radius {:keys [angle]} right? y anchor]
  (let [dx (js/Math.sin angle)
        dy (- (js/Math.cos angle))
        xr (when (and (= right? (pos? dx)) (pos? (* y dy)))
             (abs (* dx (/ y dy))))]
    (if (and xr (<= xr (+ anchor 0.5)))
      (min xr anchor)
      (ring-x ring-radius y))))

(defn- top-to-bottom
  "A side's slices in top-to-bottom label order: clockwise on the right,
   counter-clockwise on the left. Angles are unwrapped around the side's
   middle, so slices that crossed over the top stay above the rest."
  [right? slices]
  (sort-by (fn [{:keys [angle]}]
             (if right?
               (if (> angle (* 0.75 tau)) (- angle tau) angle)
               (- (if (< angle (* 0.25 tau)) (+ angle tau) angle))))
           slices))

(defn- stack-side
  "Heights for one side's labels, and how far they strayed from their
   slices (squared), with overflow past the bottom edge as a heavy cost."
  [right? slices {:keys [spacing y-min y-max] :as ctx}]
  (let [ordered (top-to-bottom right? slices)
        ideals  (map (partial ideal-y ctx) ordered)
        ys      (vec (distribute ideals spacing y-min y-max))]
    {:right?  right?
     :ordered ordered
     :ys      ys
     :cost    (reduce + (map (fn [y ideal]
                               (+ (js/Math.pow (- y ideal) 2)
                                  (* 1e6 (max 0 (- y y-max)))))
                             ys ideals))}))

(defn- split-sides
  "Splits slices between the sides. By default a slice's label goes on
   the side of its mid-angle; top-shift/bottom-shift move that many
   slices nearest 12/6 o'clock across: positive moves left-side slices
   to the right, negative moves right-side slices to the left."
  [slices near top-shift bottom-shift]
  (let [moved (set (concat (take top-shift (:top-left near))
                           (take (- top-shift) (:top-right near))
                           (take bottom-shift (:bottom-left near))
                           (take (- bottom-shift) (:bottom-right near))))]
    (group-by (fn [{:keys [angle] :as slice}]
                (not= (< angle (/ tau 2)) (contains? moved slice)))
              slices)))

(defn- best-layout
  "Stacks both sides for every allowed split and keeps the one whose
   labels stay closest to their slices. Each label moved across costs
   `spacing`², so labels only switch sides when that clearly helps —
   e.g. a cluster of small slices at the top of the left side, while the
   top of the right side is free."
  [slices {:keys [max-cross-angle spacing] :as ctx}]
  (let [in-range (fn [pred order] (sort-by :angle order (filter (comp pred :angle) slices)))
        half     (/ tau 2)
        near     {:top-left     (in-range #(> % (- tau max-cross-angle)) >)
                  :top-right    (in-range #(< % max-cross-angle) <)
                  :bottom-right (in-range #(< (- half max-cross-angle) % half) >)
                  :bottom-left  (in-range #(<= half % (+ half max-cross-angle)) <)}
        shifts   (fn [neg pos] (range (- (count (neg near))) (inc (count (pos near)))))]
    (apply min-key :cost
           (for [top-shift    (shifts :top-right :top-left)
                 bottom-shift (shifts :bottom-right :bottom-left)]
             (let [sides (map (fn [[right? side]] (stack-side right? side ctx))
                              (split-sides slices near top-shift bottom-shift))]
               {:sides sides
                :cost  (+ (reduce + (map :cost sides))
                          (* spacing spacing (+ (abs top-shift) (abs bottom-shift))))})))))

(defn layout-labels
  "Label positions for pie slices, in the pie-center frame.

   slices — seq of {:key k :angle a}, a = slice mid-angle, clockwise from
            12 o'clock (d3.pie's convention).

   Returns {k {:x :y :right? :bend-x}}: x/y is the label anchor (where
   the text starts and its connector ends), bend-x is where the connector
   leaves the ring and turns horizontal at height y."
  [slices {:keys [ring-radius bounds label-height spacing gap stagger-step max-cross-angle]}]
  (let [{:keys [half-w top bottom]} bounds
        half (/ label-height 2)
        ctx  {:ring-radius     ring-radius
              :label-height    label-height
              :gap             gap
              :spacing         spacing
              :max-cross-angle max-cross-angle
              :y-min           (+ (- top) half)
              :y-max           (- bottom half)}]
    (into {}
          (for [{:keys [right? ordered ys]} (:sides (best-layout slices ctx))
                :let [sign (if right? 1 -1)
                      ds   (stagger (map (fn [y] (anchor-x ring-radius label-height gap y)) ys)
                                    ys spacing stagger-step)]
                [{:keys [key] :as slice} y d] (map vector ordered ys ds)
                :let [anchor (min half-w d)]]
            [key {:x      (* sign anchor)
                  :y      y
                  :bend-x (* sign (bend-x ring-radius slice right? y anchor))
                  :right? right?}]))))
